import type { PuttMark } from './tileViewModel'

/**
 * B40 — THE GOLFER'S THOUGHT.
 *
 * Owner: "could we maybe add a little speech bubble that shows up sometimes with
 * some funny word a golfer would say when they hit, miss or about to etc.? so it
 * feels like his thoughts in a very few words".
 *
 * ═══ THE ONE RULE THAT IS NOT A DESIGN CHOICE ═══════════════════════════
 *
 * A PRE-PUTT LINE MAY NOT KNOW THE OUTCOME. Birdie's whole fairness story is
 * that the round is committed before betting opens and hidden until it plays; a
 * bubble drawn from a pool that varied with what was about to happen — anything
 * that read as confidence or doubt — would tell a player the result early, on
 * the screen their money is on. So the pre-putt pool is ONE pool, disjoint from
 * every reaction pool, and `pickThought` is a pure function whose inputs cannot
 * carry a result: the moment, the golfer, and two injected numbers. There is no
 * argument it could read the outcome from even if someone tried.
 *
 * `golferThoughtPools` proves the disjointness and `golferThought.test.ts`
 * asserts it, so a line added to the wrong list fails the build rather than the
 * fairness story.
 *
 * ═══ WHY IT REPEATS ITSELF ══════════════════════════════════════════════
 *
 * Owner, on expanding the pool: "i guess a golfer would also often say the same
 * thing in their head etc." — which is the better idea and changes the design.
 * Repetition is not a defect here, it is the characterisation. Each golfer gets
 * a HABIT: a couple of favoured lines per moment, derived from a hash of
 * something stable about them, weighted well above the rest of the pool. Demo
 * Pro then mutters the same two things round after round and a regular player
 * comes to know them, which is what makes a small pool read as a person rather
 * than as a random generator.
 *
 * Deterministic, with no state and nothing stored: the same golfer gets the same
 * habit on every device and after every reload.
 *
 * ═══ ADDING A LINE ══════════════════════════════════════════════════════
 *
 * One row in `BIRDIE_THOUGHTS` below, one branch in `golferThoughtCopy`'s switch
 * (the i18n extractor is literal-only — A25 — so the English must be written out
 * as the first argument of a `t()` call), and the line in all sixteen locales in
 * `scripts/birdieLocales.mjs`. No logic changes and no test changes beyond the
 * count.
 */

/**
 * When a thought can arrive.
 *
 * `pre` is the only one that runs blind. The three `w*` moments are the round's
 * WEATHER, and they carry the hardest version of the spoiler rule in this
 * feature — see `THE ROUND TYPE IS A BETTABLE MARKET` below.
 */
export type ThoughtMoment =
  | 'pre'
  | 'in1'
  | 'in2'
  | 'in3'
  | 'miss'
  | 'nearMiss'
  | 'wFire'
  | 'wFrost'
  | 'wPlain'

/**
 * ═══ THE ROUND TYPE IS A BETTABLE MARKET ════════════════════════════════
 *
 * Frost and Fire are OPTIONS, priced on the board, that players put money on. A
 * line hinting at the type before the type is public would not merely spoil a
 * surprise — it would hand one player the answer to a market others are still
 * betting into. That is a different and worse class of problem than the putt
 * lines, and "a plain day" is not exempt: saying the round is ordinary is saying
 * Frost and Fire are both dead, which is the same fact about the same market.
 *
 * So a weather line may fire ONLY from the REVEALED type — the one that arrives
 * on the putt rows mid-clip, seconds past the bet cutoff, and the same signal
 * the board resolves its own Frost/Fire tiles from (`liveStatus.roundTypeAlive`,
 * `livePutts.RevealedRoundType`). Never the drawn type, never anything on
 * `race_ready`.
 *
 * It is structural rather than remembered: `thoughtMomentFor` cannot return a
 * weather moment without a revealed type in its hand, and `golferThought.test.ts`
 * sweeps every other input to prove no combination of them produces one.
 */
export const WEATHER_MOMENTS: readonly ThoughtMoment[] = ['wFire', 'wFrost', 'wPlain']

/** The moment for a revealed type, or nothing. The ONLY way into that pool. */
export function weatherMoment(
  revealed: 'plain' | 'frost' | 'fire' | undefined
): ThoughtMoment | undefined {
  if (revealed === 'fire') return 'wFire'
  if (revealed === 'frost') return 'wFrost'
  if (revealed === 'plain') return 'wPlain'
  return undefined
}

/** The moment that happens BEFORE a ball is struck — see the header. */
export const PRE_PUTT_MOMENT: ThoughtMoment = 'pre'

export interface ThoughtLine {
  /** Stable id. The copy module resolves it to a localised literal. */
  id: string
  moment: ThoughtMoment
}

/**
 * The pool, as data.
 *
 * Fourteen lines, one to three words each, and every one of them an
 * INTERJECTION rather than a joke. That constraint is what makes sixteen
 * locales tractable: a pun has to be re-invented in every language and a
 * reaction does not — every language already has its own natural thing to say
 * when a ball drops or lips out. Nothing here depends on English, on wordplay,
 * or on cultural context, and nothing comments on the player, their money or
 * whether they won: it is the golfer's round, not theirs.
 */
export const BIRDIE_THOUGHTS: readonly ThoughtLine[] = [
  // Before the ball — knows nothing, and must not. `Read it.` was here and was
  // cut: on screen it read as an instruction to the PLAYER rather than as the
  // golfer's own thought, which is the one thing this pool may never do.
  { id: 'steady', moment: 'pre' },
  { id: 'rightThen', moment: 'pre' },
  // The first one drops: pleased, and early.
  { id: 'yes', moment: 'in1' },
  { id: 'thatsIt', moment: 'in1' },
  // The second: building.
  { id: 'two', moment: 'in2' },
  { id: 'rollingNow', moment: 'in2' },
  // All three home — the biggest moment the game has.
  { id: 'allThree', moment: 'in3' },
  { id: 'getIn', moment: 'in3' },
  // A miss: flat, and never a comment on anyone's money.
  { id: 'ohh', moment: 'miss' },
  { id: 'justShort', moment: 'miss' },
  { id: 'notThisTime', moment: 'miss' },
  // Two in and the third lips out. The most characterful moment in the feature.
  { id: 'soClose', moment: 'nearMiss' },
  { id: 'nearly', moment: 'nearMiss' },
  // The round's WEATHER, once it is public. These are the only lines in the pool
  // that are about a MARKET rather than about a golf shot, which is why the gate
  // above them is the strictest thing in this module.
  { id: 'onFire', moment: 'wFire' },
  { id: 'feelHeat', moment: 'wFire' },
  { id: 'gettingCold', moment: 'wFrost' },
  { id: 'iceOut', moment: 'wFrost' },
  { id: 'plainDay', moment: 'wPlain' },
  { id: 'justGolf', moment: 'wPlain' }
]

/**
 * ═══ HOW OFTEN HE SPEAKS: THE SURPRISE CURVE ════════════════════════════
 *
 * Owner: "on a low make chance, like below 40% and simply gets one in, it could
 * say something as it doesn't happen too often … so it feels more alive and how
 * the golfer would feel."
 *
 * That generalises, and the generalisation replaced a ladder of named cases:
 * HOW LIKELY HE IS TO SPEAK TRACKS HOW UNLIKELY THE THING THAT JUST HAPPENED
 * WAS. A 20 % golfer holing his first is a genuine event and he nearly always
 * says something; a 70 % golfer holing his first is a Tuesday. A 70 % golfer
 * MISSING is notable the other way, and gets a word for the same reason — which
 * the ladder never would have.
 *
 * The number is already on the card. The chance of the sequence so far is
 * `p^holed × (1-p)^missed`, and the rate is a straight line from the floor at
 * something certain to the ceiling at something that should not have happened:
 *
 *   one in at 70 %   p = .70        surprise .30   →  rate .33
 *   one in at 20 %   p = .20        surprise .80   →  rate .70
 *   two in at 70 %   p = .49        surprise .51   →  rate .48
 *   two in at 20 %   p = .04        surprise .96   →  rate .82
 *   a miss at 70 %   p = .30        surprise .70   →  rate .63
 *   a miss at 20 %   p = .80        surprise .20   →  rate .25
 *
 * IT BEHAVES AT THE EDGES, which is the thing that would have sent this back to
 * a ladder. At a make rate of 1 a make is certain and sits on the floor while a
 * miss sits on the ceiling; at 0 the two swap. A round with no card has no make
 * chance at all, and stands on a neutral surprise rather than dividing by
 * nothing.
 *
 * The two always-worth-a-line moments are left GUARANTEED rather than computed.
 * The curve would pick them anyway — they are the rarest things the game
 * produces — but the owner should not have to trust a formula for the two
 * moments that matter most.
 */
export const THOUGHT_RATE_FLOOR = 0.1
export const THOUGHT_RATE_CEILING = 0.85

/**
 * The line before a ball is struck has no event to be surprised by, so it takes
 * a flat rate of its own — and a low one. It is the least interesting thing he
 * says and it was the one being seen most.
 */
export const THOUGHT_PRE_RATE = 0.12

/**
 * The weather. Frost and Fire are rare by design and worth remarking on; a plain
 * day is the norm and mostly passes without comment. Their probabilities are the
 * engine's and are not published to the widget, so these are flat rather than
 * computed — the one place the curve could not reach.
 */
export const THOUGHT_WEATHER_RATE = 0.7
export const THOUGHT_PLAIN_RATE = 0.2

/** A round with no card has no make chance. Neither certain nor astonishing. */
export const THOUGHT_NEUTRAL_SURPRISE = 0.5

/**
 * ═══ HOW MANY LINES A ROUND EARNS ═══════════════════════════════════════
 *
 * Owner: "sometimes it would make sense, e.g. one in, two in, all in … but not
 * always, maybe if it's a hard round etc." — so the flat one-per-round cap is a
 * BUDGET the round earns instead.
 *
 * The principle is that he is quiet through an ordinary round and finds his
 * voice when something is actually happening, which is also what makes a
 * talkative round feel earned rather than chatty. Both levers are data the board
 * already holds: how likely the putt was, and how many have dropped.
 *
 *   ordinary round                    1  (exactly what it did before)
 *   hard round                        2
 *   hard round with a streak running  3  — one per ball, the ceiling
 *   easy round with a streak running  2
 *
 * A 70 % golfer holing the first is not worth a word; a 25 % golfer holing
 * anything is. THE THRESHOLD IS THE ARGUABLE PART and it is one number, here.
 */
export const THOUGHT_HARD_MAKE_RATE = 0.4

export const THOUGHT_BUDGET_ORDINARY = 1
export const THOUGHT_BUDGET_MAX = 3

/**
 * The moments that are worth a line whatever the budget says: TWO BALLS IN, all
 * three home, and the third lipping out after two were in.
 *
 * Two-in joined them on the owner's read — "a 70 chance and two goes in hole is
 * still worth saying two in maybe?" — and he is right: a streak is the thing a
 * golfer reacts to, and whether it is acknowledged should not depend on how hard
 * the round was. `THOUGHT_HARD_MAKE_RATE` now buys only the EXTRA budgeted line
 * beyond it.
 *
 * They override the BUDGET and the between-rounds cooldown — both of which are
 * rate limits — and they override nothing else. The overlap rule and the two
 * no-repeat rules still hold over them, because those are not about how often he
 * speaks but about whether the board is readable and whether he sounds broken.
 */
export const THOUGHT_ALWAYS: readonly ThoughtMoment[] = ['in2', 'in3', 'nearMiss']

/**
 * ═══ HOW LONG A BUBBLE STANDS ═══════════════════════════════════════════
 *
 * Counted from when the line finishes TYPING, not from when the box appears:
 * the entrance and the reveal together eat most of a second before there are
 * words to read, so a "life" measured from the first frame overstates how long
 * the thought is legible by exactly that much.
 *
 *   enter (0.46 s)  →  type (0.32 s)  →  READ (2.6 s)  =  3.38 s on screen
 *
 * THE CEILING IS THE PUTT SPACING. The engine emits a putt row from the clip's
 * own frames about two seconds behind it, and on staging the rows land roughly
 * 4.5 s apart. One bubble at a time is a hard rule, so a life that ran past that
 * gap would silently suppress the next ball's line — which is the mechanism that
 * was starving the weather line. 3.38 s clears with better than a second in
 * hand, and `golferThought.test.ts` fails if that margin is ever spent.
 */
export const THOUGHT_ENTER_MS = 460
export const THOUGHT_READ_MS = 2600

/** Measured on staging: the gap between consecutive putt rows, ms. */
export const BIRDIE_PUTT_GAP_MS = 4500

/**
 * The chance of the putts so far, given how likely each one was to drop.
 *
 * `p^holed × (1-p)^missed`. Clamped into [0,1] and defended against a make rate
 * that is not a number, because it arrives from a card that a round may not
 * carry.
 */
export function sequenceChance(marks: readonly PuttMark[], makeRate: number): number {
  if (!Number.isFinite(makeRate)) return Number.NaN
  const p = Math.max(0, Math.min(1, makeRate))
  let chance = 1
  for (const mark of marks) chance *= mark === 'holed' ? p : 1 - p
  return chance
}

/** How unlikely the thing that just happened was, 0 (certain) to 1 (astonishing). */
export function eventSurprise(marks: readonly PuttMark[], makeRate?: number): number {
  if (marks.length === 0 || makeRate === undefined) return THOUGHT_NEUTRAL_SURPRISE
  const chance = sequenceChance(marks, makeRate)
  if (!Number.isFinite(chance)) return THOUGHT_NEUTRAL_SURPRISE
  return Math.max(0, Math.min(1, 1 - chance))
}

/**
 * How likely this moment is to produce a line at all.
 *
 * THE PRE-PUTT ANSWER IS A CONSTANT, whatever else is passed. That is the
 * fairness rule from the module header expressed in the one place a rate could
 * otherwise leak an outcome into: the marks and the make chance are ignored for
 * that moment, and `golferThought.test.ts` sweeps every combination of them to
 * prove the answer never moves.
 */
export function rateFor(input: {
  moment: ThoughtMoment
  marks?: readonly PuttMark[]
  makeRate?: number
}): number {
  if (input.moment === PRE_PUTT_MOMENT) return THOUGHT_PRE_RATE
  if (input.moment === 'wPlain') return THOUGHT_PLAIN_RATE
  if (input.moment === 'wFire' || input.moment === 'wFrost') return THOUGHT_WEATHER_RATE
  const surprise = eventSurprise(input.marks ?? [], input.makeRate)
  return THOUGHT_RATE_FLOOR + (THOUGHT_RATE_CEILING - THOUGHT_RATE_FLOOR) * surprise
}

/**
 * The lines this round has earned.
 *
 * PACING, not reaction — how often he speaks is the curve's job now; this is
 * only how many different moments one round may speak at. A hard round earns a
 * second, which is what lets it carry a tee-off line and a ball reaction rather
 * than choosing between them.
 *
 * The streak bonus that used to live here is gone: two-in is an always-worth-a-
 * line moment now and ignores the budget, and after two are down every remaining
 * moment is one of the other two that also ignore it — so the grant could never
 * be spent.
 */
export function thoughtBudget(input: { makeRate?: number }): number {
  const rate = input.makeRate
  const hard = rate !== undefined && Number.isFinite(rate) && rate <= THOUGHT_HARD_MAKE_RATE
  return Math.min(THOUGHT_BUDGET_MAX, THOUGHT_BUDGET_ORDINARY + (hard ? 1 : 0))
}

/** Whether this moment is worth a line whatever the budget says. */
export function alwaysWorthALine(moment: ThoughtMoment): boolean {
  return THOUGHT_ALWAYS.indexOf(moment) > -1
}

/** Lines a golfer favours per moment. Two is a habit; one is a catchphrase. */
export const THOUGHT_HABIT_SIZE = 2

/** How often a bubble comes from the golfer's own habit rather than the pool. */
export const THOUGHT_HABIT_WEIGHT = 0.72

/** The lines for one moment, in table order. */
export function golferThoughtPools(moment: ThoughtMoment): readonly ThoughtLine[] {
  return BIRDIE_THOUGHTS.filter((line) => line.moment === moment)
}

/**
 * A small, stable hash of a string — FNV-1a, 32-bit, written out because the
 * widget has none and a golfer's habit must be the same on every device.
 */
export function thoughtHash(value: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    // `Math.imul` rather than `*`: the product overflows a double's exact
    // integer range and the low bits — the only ones that matter — would drift.
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/**
 * The lines THIS golfer favours at this moment.
 *
 * Deterministic in the golfer alone, so his habit is the same every round and
 * on every device, and different golfers land on different lines because the
 * hash is different. Never the whole pool: a habit that covered everything would
 * not be a habit.
 */
export function golferHabit(
  golferKey: string,
  moment: ThoughtMoment,
  size: number = THOUGHT_HABIT_SIZE
): readonly ThoughtLine[] {
  const pool = golferThoughtPools(moment)
  if (pool.length <= 1) return pool
  const want = Math.max(1, Math.min(size, pool.length - 1))
  const seed = thoughtHash(`${golferKey}:${moment}`)
  const out: ThoughtLine[] = []
  for (let i = 0; i < want; i++) {
    // Step by a number coprime with nothing in particular — it only has to
    // spread, and `pool.length` is two or three.
    const index = (seed + i * 7) % pool.length
    const line = pool[index]
    if (line !== undefined && out.indexOf(line) === -1) out.push(line)
  }
  return out
}

export interface ThoughtInput {
  /**
   * WHICH MOMENT. For `pre` this is the only thing known, and it is knowable
   * before a ball is struck — see the header. Every other value is a reaction to
   * something that has already happened and may say so.
   */
  moment: ThoughtMoment
  /** Something stable about the golfer. His name — it is what a player knows him by. */
  golferKey: string
  /** The line the last bubble showed, so the same one never lands twice running. */
  lastLineId?: string
  /** Every line this round has already used — he does not repeat himself inside one. */
  usedLineIds?: readonly string[]
  /**
   * Every MOMENT this round has already spoken at.
   *
   * THE DEFECT THIS EXISTS FOR. A moment is a render-time condition, not an
   * event: `pre` is true for the whole tee-off window, `in1` for as long as one
   * ball is the newest. So when a bubble's life ended the same moment was still
   * true, the picker ran again, and on a hard round — where the budget is two —
   * it spent BOTH lines on the tee-off window before a ball had been struck.
   * "Steady." followed by "Right then.", which is exactly what the owner saw,
   * and which also left nothing for the ball that followed.
   *
   * The budget exists so a round can speak at several DIFFERENT moments. One
   * line per moment is the rule that makes that true, and it binds the
   * always-worth-a-line moments as well — they each happen once in a round
   * anyway.
   */
  usedMoments?: readonly ThoughtMoment[]
  /** Whether the PREVIOUS round showed one, so two never land back to back. */
  spokeLastRound?: boolean
  /**
   * TRUE WHILE A BUBBLE IS STILL ON SCREEN. One at a time, always: with the
   * balls seconds apart this is the rule that stops a talkative round becoming a
   * commentary track, and it is the one thing the always-worth-a-line moments
   * cannot override — two bubbles at once is not a golfer thinking, it is a
   * notification stack.
   */
  bubbleUp?: boolean
  /** How many lines this round has already spent. */
  spokenThisRound?: number
  /** How many it earned (`thoughtBudget`). Defaults to the ordinary one. */
  budget?: number
  /**
   * How likely this moment is to produce a line — `rateFor`. Passed in rather
   * than computed here, so nothing about a RESULT enters this function's
   * signature: for the pre-putt moment the caller can only ever have a constant
   * to pass.
   */
  rate: number
  /** 0..1 — whether a bubble happens at all, and whether it comes from the habit. */
  roll: number
  /** 0..1 — which line, once the pool is chosen. */
  pick: number
}

/**
 * The line for this moment, or nothing — which is most of the time.
 *
 * Pure. The only way to get a different answer is to pass different arguments,
 * and none of the arguments can carry a result.
 */
export function pickThought(input: ThoughtInput): ThoughtLine | undefined {
  if (!(input.roll >= 0) || !(input.pick >= 0)) return undefined

  // ONE AT A TIME, whatever else is true. Checked before everything, including
  // the two moments that are otherwise always worth a line.
  if (input.bubbleUp === true) return undefined

  // ONE LINE PER MOMENT, and it binds everything below including the moments
  // that otherwise ignore the budget. A moment is a condition that stays true
  // for seconds; without this the picker simply runs again when the bubble's
  // life ends.
  if ((input.usedMoments ?? []).indexOf(input.moment) > -1) return undefined

  const always = alwaysWorthALine(input.moment)
  if (!always) {
    // A joke that always arrives stops being one, and one that arrives two
    // rounds running is a running commentary.
    if (input.spokeLastRound === true) return undefined
    const spent = input.spokenThisRound ?? 0
    if (spent >= (input.budget ?? THOUGHT_BUDGET_ORDINARY)) return undefined
    if (input.roll >= input.rate) return undefined
  }

  const used = input.usedLineIds ?? []
  const pool = golferThoughtPools(input.moment).filter(
    (line) => line.id !== input.lastLineId && used.indexOf(line.id) === -1
  )
  if (pool.length === 0) return undefined

  // The habit, and the rest. On the ordinary path `roll` has already been
  // through the rate gate, so it is rescaled across it rather than re-used as a
  // raw fraction — otherwise the habit would only ever see the bottom third of
  // its own range. On the always-worth-a-line path it never met that gate, so it
  // is already uniform and is used as it is.
  const scaled = always ? input.roll : input.roll / Math.max(input.rate, 0.0001)
  const habit = golferHabit(input.golferKey, input.moment).filter((line) => pool.indexOf(line) > -1)
  const rest = pool.filter((line) => habit.indexOf(line) === -1)
  const from = scaled < THOUGHT_HABIT_WEIGHT && habit.length > 0 ? habit : rest
  const chosen = from.length > 0 ? from : pool
  const index = Math.min(chosen.length - 1, Math.floor(input.pick * chosen.length))
  return chosen[index]
}

/**
 * ═══ THE TYPEWRITER ═════════════════════════════════════════════════════
 *
 * Owner: "maybe we could have a typewriter effect on it so it feels more like
 * thoughts". It is a good instinct — letter by letter is how an interior voice
 * reads on screen — and at one to three words the whole effect lives inside a
 * third of a second, so the risks are all about how it is built rather than
 * whether it is nice.
 *
 * A WIDTH MASK, NOT A STRING BEING APPENDED. The text is laid out once, in full,
 * and revealed by a clip. Two things follow, and both were the alternative's
 * problems:
 *  - THE BOX IS ITS FINAL SIZE ON THE FIRST FRAME, so the bubble cannot grow as
 *    it types and cannot jitter the capsule it is anchored inside.
 *  - IT CANNOT BREAK A SCRIPT. Revealing by JS character index breaks Arabic's
 *    joined forms and cuts Thai combining marks off their bases; a clip over
 *    already-laid-out text cannot, because the shaping has already happened.
 *
 * THE STEP COUNT IS WHAT MAKES IT A TYPEWRITER rather than a wipe: the clip is
 * animated with `steps(n)`, n from the line's own length, over a FIXED total —
 * so "Ohh." and "Not this time." finish in the same beat and the long one does
 * not drag. A step may land mid-glyph for one frame; that is a clip, not a
 * break, and at ~30 ms it is not visible.
 */

/** The reveal's own beat, in the motion scale's terms — `$birdie-t-state`. */
export const THOUGHT_TYPE_MS = 320

/** Fewest and most steps, so a three-letter line still types and a long one does not stutter. */
export const THOUGHT_TYPE_MIN_STEPS = 4
export const THOUGHT_TYPE_MAX_STEPS = 14

/**
 * How many steps this line types in.
 *
 * `Array.from` iterates CODE POINTS rather than UTF-16 units, so a CJK character
 * outside the basic plane counts once rather than twice. It is only a step
 * count — nothing is ever split on it — so an approximate answer for combining
 * marks costs nothing.
 */
export function thoughtTypeSteps(text: string): number {
  const length = Array.from(text.trim()).length
  return Math.max(THOUGHT_TYPE_MIN_STEPS, Math.min(THOUGHT_TYPE_MAX_STEPS, length))
}

/** How long one bubble stays on screen, ms — see `THOUGHT_READ_MS` above. */
export const THOUGHT_LIFE_MS = THOUGHT_ENTER_MS + THOUGHT_TYPE_MS + THOUGHT_READ_MS
